<template>
  <div class="purchase-order-container">
    <div class="page-header">
      <div>
        <h1>{{ $t('PurchaseOrder.Index.line5_text') }}</h1>
        <p class="subtitle">{{ $t('PurchaseOrder.Index.line6_text') }}</p>
      </div>
      <router-link to="/purchase-order/create" class="btn btn-primary">
        <i class="icon-plus"></i> 新建采购单
      </router-link>
    </div>
    <!-- 搜索和筛选 -->
    <div class="filter-section">
      <div class="filter-row">
        <div class="filter-item">
          <label>{{ $t('PurchaseOrder.Index.line16_text') }}</label>
          <input v-model="filters.orderNo" type="text" :placeholder="$t('PurchaseOrder.Index.line17_placeholder')" class="input" @keyup.enter="loadOrders" />
        </div>
        <div class="filter-item">
          <label>{{ $t('PurchaseOrder.Index.line20_text') }}</label>
          <select v-model="filters.sellerId" class="input" @change="loadOrders">
            <option value="">{{ $t('PurchaseOrder.Index.line22_text') }}</option>
            <option v-for="seller in sellers" :key="seller.id" :value="seller.id">
              {{ seller.customerName }}
            </option>
          </select>
        </div>
        <div class="filter-item">
          <label>{{ $t('PurchaseOrder.Index.line29_text') }}</label>
          <select v-model="filters.orderStatus" class="input" @change="loadOrders">
            <option value="">{{ $t('PurchaseOrder.Index.line31_text') }}</option>
            <option value="0">{{ $t('PurchaseOrder.Index.line32_text') }}</option>
            <option value="1">{{ $t('PurchaseOrder.Index.line33_text') }}</option>
            <option value="2">{{ $t('PurchaseOrder.Index.line34_text') }}</option>
            <option value="3">{{ $t('PurchaseOrder.Index.line35_text') }}</option>
            <option value="4">{{ $t('PurchaseOrder.Index.line36_text') }}</option>
            <option value="5">{{ $t('PurchaseOrder.Index.line37_text') }}</option>
            <option value="-1">{{ $t('PurchaseOrder.Index.line38_text') }}</option>
          </select>
        </div>
        <div class="filter-item">
          <label>{{ $t('PurchaseOrder.Index.line42_text') }}</label>
          <select v-model="filters.paymentStatus" class="input" @change="loadOrders">
            <option value="">{{ $t('PurchaseOrder.Index.line44_text') }}</option>
            <option value="0">{{ $t('PurchaseOrder.Index.line45_text') }}</option>
            <option value="1">{{ $t('PurchaseOrder.Index.line46_text') }}</option>
            <option value="2">{{ $t('PurchaseOrder.Index.line47_text') }}</option>
          </select>
        </div>
        <div class="filter-item">
          <label>{{ $t('PurchaseOrder.Index.line51_text') }}</label>
          <input v-model="filters.startDate" type="date" class="input" @change="loadOrders" />
        </div>
        <div class="filter-item">
          <label>{{ $t('PurchaseOrder.Index.line55_text') }}</label>
          <input v-model="filters.endDate" type="date" class="input" @change="loadOrders" />
        </div>
        <div class="filter-actions">
          <button class="btn btn-secondary" @click="resetFilters">
            <i class="icon-refresh"></i> 重置
          </button>
        </div>
      </div>
    </div>
    <!-- 订单列表 -->
    <div class="orders-section">
      <div class="section-header">
        <span>共找到 {{ totalCount }} 个订单</span>
        <div class="view-options">
          <button :class="['view-btn', { active: viewType === 'list' }]" @click="viewType = 'list'">
            <i class="icon-list"></i>
          </button>
          <button :class="['view-btn', { active: viewType === 'card' }]" @click="viewType = 'card'">
            <i class="icon-grid"></i>
          </button>
        </div>
      </div>
      <!-- 列表视图 -->
      <table v-if="viewType === 'list'" class="orders-table">
        <thead>
          <tr>
            <th>{{ $t('PurchaseOrder.Index.line82_text') }}</th>
            <th>{{ $t('PurchaseOrder.Index.line83_text') }}</th>
            <th>{{ $t('PurchaseOrder.Index.line84_text') }}</th>
            <th>{{ $t('PurchaseOrder.Index.line85_text') }}</th>
            <th>{{ $t('PurchaseOrder.Index.line86_text') }}</th>
            <th>{{ $t('PurchaseOrder.Index.line87_text') }}</th>
            <th>{{ $t('PurchaseOrder.Index.line88_text') }}</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="order in orders" :key="order.id" class="order-row">
            <td>
              <div class="order-no">{{ order.purchaseOrderNo }}</div>
            </td>
            <td>
              <div class="seller-name">{{ order.sellerShopName }}</div>
            </td>
            <td>
              <span class="amount">¥{{ order.totalAmount }}</span>
            </td>
            <td>
              <span class="status-badge" :class="getOrderStatusClass(order.orderStatus)">
                {{ getOrderStatusLabel(order.orderStatus) }}
              </span>
            </td>
            <td>
              <span class="status-badge" :class="getPaymentStatusClass(order.paymentStatus)">
                {{ getPaymentStatusLabel(order.paymentStatus) }}
              </span>
            </td>
            <td>
              <span class="time">{{ formatDate(order.createdAt) }}</span>
            </td>
            <td>
              <div class="action-buttons">
                <router-link :to="`/purchase-order/${order.id}`" class="btn-action" :title="$t('common.viewDetail')">
                  <i class="icon-eye"></i>
                </router-link>
                <button v-if="order.orderStatus === 0" class="btn-action" @click="editOrder(order)" :title="$t('common.edit')">
                  <i class="icon-edit"></i>
                </button>
                <button v-if="order.orderStatus === 0" class="btn-action danger" @click="deleteOrder(order.id)" :title="$t('common.delete')">
                  <i class="icon-trash"></i>
                </button>
                <button v-if="order.paymentStatus !== 2 && order.orderStatus >= 1" class="btn-action success" @click="goToPay(order.id)" :title="$t('PurchaseOrder.Index.line126_title')">
                  <i class="icon-credit-card"></i>
                </button>
              </div>
            </td>
          </tr>
        </tbody>
      </table>
      <!-- 卡片视图 -->
      <div v-else class="orders-card-view">
        <div v-for="order in orders" :key="order.id" class="order-card">
          <div class="card-header">
            <div>
              <h4 class="order-no">{{ order.purchaseOrderNo }}</h4>
              <p class="seller-name">{{ order.sellerShopName }}</p>
            </div>
            <div class="card-actions">
              <button class="icon-btn" @click="showOrderMenu(order.id)">
                <i class="icon-more-vertical"></i>
              </button>
            </div>
          </div>
          <div class="card-content">
            <div class="info-row">
              <span class="label">{{ $t('PurchaseOrder.Index.line150_text') }}</span>
              <span class="value amount">¥{{ order.totalAmount }}</span>
            </div>
            <div class="info-row">
              <span class="label">{{ $t('PurchaseOrder.Index.line154_text') }}</span>
              <span class="value">
                <span class="status-badge" :class="getOrderStatusClass(order.orderStatus)">
                  {{ getOrderStatusLabel(order.orderStatus) }}
                </span>
              </span>
            </div>
            <div class="info-row">
              <span class="label">{{ $t('PurchaseOrder.Index.line162_text') }}</span>
              <span class="value">
                <span class="status-badge" :class="getPaymentStatusClass(order.paymentStatus)">
                  {{ getPaymentStatusLabel(order.paymentStatus) }}
                </span>
              </span>
            </div>
            <div class="info-row">
              <span class="label">{{ $t('PurchaseOrder.Index.line170_text') }}</span>
              <span class="value">{{ formatDate(order.createdAt) }}</span>
            </div>
          </div>
          <div class="card-footer">
            <router-link :to="`/purchase-order/${order.id}`" class="btn btn-sm btn-primary">
              查看详情
            </router-link>
          </div>
        </div>
      </div>
      <!-- 分页 -->
      <div v-if="totalCount > 0" class="pagination">
        <button :disabled="currentPage === 1" @click="currentPage--" class="btn btn-sm">
          <i class="icon-chevron-left"></i> 上一页
        </button>
        <div class="page-info">
          第 {{ currentPage }} / {{ totalPages }} 页
        </div>
        <button :disabled="currentPage === totalPages" @click="currentPage++" class="btn btn-sm">
          下一页 <i class="icon-chevron-right"></i>
        </button>
      </div>
    </div>
    <!-- 空状态 -->
    <div v-if="orders.length === 0 && !loading" class="empty-state">
      <div class="empty-icon">
        <i class="icon-inbox"></i>
      </div>
      <h3>{{ $t('PurchaseOrder.Index.line199_text') }}</h3>
      <p>{{ $t('PurchaseOrder.Index.line200_text') }}</p>
      <router-link to="/purchase-order/create" class="btn btn-primary">
        创建采购单
      </router-link>
    </div>
    <!-- 加载状态 -->
    <div v-if="loading" class="loading-state">
      <div class="spinner"></div>
      <p>{{ $t('common.loading') }}</p>
    </div>
  </div>
</template>

<script>
import { mapActions } from 'vuex';
import { formatDate } from '@/utils/dateUtils';

export default {
  name: 'PurchaseOrderList',
  data() {
    return {
      filters: {
        orderNo: '',
        sellerId: '',
        orderStatus: '',
        paymentStatus: '',
        startDate: '',
        endDate: ''
      },
      orders: [],
      sellers: [],
      currentPage: 1,
      pageSize: 10,
      totalCount: 0,
      totalPages: 0,
      viewType: 'list',
      loading: false,
      orderStatusMap: {
        0: '草稿',
        1: '已提交',
        2: '已确认',
        3: '已发货',
        4: '已收货',
        5: '已完成',
        '-1': '已取消'
      },
      paymentStatusMap: {
        0: '待支付',
        1: '部分支付',
        2: '已支付'
      }
    };
  },
  methods: {
    ...mapActions('purchaseOrder', [
      'getOrders',
      'deleteOrder',
      'getSellers'
    ]),
    formatDate,
    getOrderStatusLabel(status) {
      return this.orderStatusMap[status] || '未知';
    },
    getOrderStatusClass(status) {
      const classes = {
        0: 'draft',
        1: 'submitted',
        2: 'confirmed',
        3: 'shipped',
        4: 'received',
        5: 'completed',
        '-1': 'cancelled'
      };
      return classes[status] || 'default';
    },
    getPaymentStatusLabel(status) {
      return this.paymentStatusMap[status] || '未知';
    },
    getPaymentStatusClass(status) {
      const classes = {
        0: 'unpaid',
        1: 'partial',
        2: 'paid'
      };
      return classes[status] || 'default';
    },
    async loadOrders() {
      this.loading = true;
      try {
        const response = await this.getOrders({
          ...this.filters,
          pageNum: this.currentPage,
          pageSize: this.pageSize
        });
        this.orders = response.data;
        this.totalCount = response.totalCount;
        this.totalPages = response.totalPage;
      } catch (error) {
        this.$message.error('加载订单失败');
      } finally {
        this.loading = false;
      }
    },
    async loadSellers() {
      try {
        this.sellers = await this.getSellers();
      } catch (error) {
        console.error('加载商家列表失败', error);
      }
    },
    resetFilters() {
      this.filters = {
        orderNo: '',
        sellerId: '',
        orderStatus: '',
        paymentStatus: '',
        startDate: '',
        endDate: ''
      };
      this.currentPage = 1;
      this.loadOrders();
    },
    editOrder(order) {
      this.$router.push(`/purchase-order/${order.id}/edit`);
    },
    async deleteOrder(orderId) {
      if (!confirm('确定要删除此订单吗？')) {
        return;
      }
      try {
        await this.deleteOrder(orderId);
        this.$message.success('订单已删除');
        this.loadOrders();
      } catch (error) {
        this.$message.error('删除订单失败');
      }
    },
    goToPay(orderId) {
      this.$router.push(`/payment?orderId=${orderId}`);
    },
    showOrderMenu(orderId) {
      // 实现菜单逻辑
      console.log('显示订单菜单:', orderId);
    }
  },
  watch: {
    currentPage() {
      this.loadOrders();
    }
  },
  mounted() {
    this.loadOrders();
    this.loadSellers();
  }
};
</script>